/* import __COLOCATED_TEMPLATE__ from './reduce-costs-modal.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type Plan from 'embercom/models/plan';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
import { tracked } from '@glimmer/tracking';
import { PLAN_DATA } from 'embercom/lib/billing';
import type Store from '@ember-data/store';
import { Metric } from 'embercom/models/data/pricing/metric-types';
import type Breakdown from 'embercom/models/billing/price/breakdown';
import type Charge from 'embercom/models/billing/price/charge';
import { assetUrl } from '@intercom/pulse/helpers/asset-url';
import { dropTask } from 'ember-concurrency-decorators';
import { post } from 'embercom/lib/ajax';
import { taskFor } from 'ember-concurrency-ts';
import moment from 'moment-timezone';
import { SELF_SERVE_CHURN_SURVEY_ID } from 'embercom/lib/billing/surveys';
import type SubscriptionEndingService from 'embercom/services/subscription-ending-service';
import launchSurveyAndTrack from 'embercom/lib/onboarding/launch-survey-and-track';

interface Signature {
  Args: {
    isOpen: boolean;
    onModalClose: (
      nextStep:
        | 'continue_cancellation'
        | 'abort_cancellation'
        | 'churn_discount_applied'
        | 'tour_triggered'
        | 'tour_skipped',
    ) => void;
    showConfirmDiscountModal: boolean;
    setConfirmDiscountModalVisibility: (visibility: boolean) => void;
  };
}

const DISCOUNT_OPTIONS = {
  claimDiscount: 'claim-discount',
  downgradePlan: 'downgrade-plan',
  removeUnusedSeats: 'remove-unused-seats',
};

const IMAGE_ASSET_PATHS = {
  [DISCOUNT_OPTIONS.claimDiscount]: 'settings/reduce-costs-modal-discount@2x.png',
  [DISCOUNT_OPTIONS.downgradePlan]: 'settings/reduce-costs-modal-downgrade-plan@2x.png',
  [DISCOUNT_OPTIONS.removeUnusedSeats]: 'settings/reduce-costs-modal-remove-seats@2x.png',
};

export default class ReduceCostsModal extends Component<Signature> {
  @service appService: any;
  @service intercomEventService: any;
  @service declare router: RouterService;
  @service declare customerService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare notificationsService: $TSFixMe;
  @service declare subscriptionEndingService: SubscriptionEndingService;
  @tracked showEditPlanModal = false;

  constructor(owner: any, args: Signature['Args']) {
    super(owner, args);
    if (!this.banneredOption && this.availableDiscountOptions.length < 1) {
      // startSurvey(SELF_SERVE_CHURN_SURVEY_ID);
      launchSurveyAndTrack(SELF_SERVE_CHURN_SURVEY_ID, this.app, this.router);
      this.args.onModalClose('tour_triggered');
    }
  }

  get app() {
    return this.appService.app;
  }

  get isOnLowestPlan() {
    return this.app.onPricing5 && !this.availableLowerPlan;
  }

  get hasLowestSeats() {
    return this.coreSeatUsageAndPrice.seatCount <= 1;
  }

  get hasDiscountApplied() {
    return this.customerService.customer.hasLineItemDiscountsApplied;
  }

  get isOnSelfServeMonthlyPlan() {
    return (
      this.currentSubscriptionPlan?.selfServe &&
      this.customerService.customer.subscription.isBilledMonthly
    );
  }

  get planName() {
    if (this.currentSubscriptionPlan) {
      return this.intl.t(this.currentSubscriptionPlan.nameTranslationKey);
    }
    return this.intl.t('billing.settings.lose-benefits-modal.cta.plan-name-fallback');
  }

  get currentSubscriptionPlan() {
    let activePlan = this.app.allPlansOnPricingModel.find(
      (plan: Plan) => plan.billableCustomerPlan && !plan.product.addon,
    );
    if (!activePlan) {
      // If a billable plan is not found, customer is on a graduating trial plan
      // TODO: Update planName code other modals
      return this.customerService.activePlans?.find((plan: Plan) => !plan.product.addon);
    }
    return activePlan;
  }

  get nextAvailableTierId() {
    let selectedPlanData = PLAN_DATA[this.currentSubscriptionPlan?.id];
    if (selectedPlanData.upgradePath) {
      return selectedPlanData.upgradePath[0];
    }

    let nextPlanId = this.availableHigherPlan?.id;
    let previousPlanId = this.availableLowerPlan?.id;

    return previousPlanId || nextPlanId;
  }

  get availableHigherPlan() {
    let plans: Plan[] = this.app.allPlansOnPricingModel;
    let currentPlanIndex: number = plans.findIndex(
      (plan) => plan.id === this.currentSubscriptionPlan?.id,
    );
    let index = currentPlanIndex + 1;

    return plans[index];
  }

  get availableLowerPlan() {
    let plans: Plan[] = this.app.allPlansOnPricingModel;
    let currentPlanIndex: number = plans.findIndex(
      (plan) => plan.id === this.currentSubscriptionPlan?.id,
    );
    let index = currentPlanIndex - 1;

    return plans[index];
  }

  get totalSeatPrice() {
    return (this.coreSeatUsageAndPrice.seatCount - 1) * this.coreSeatUsageAndPrice.unitPrice; // Subtract 1 to account for the base seat
  }

  get coreSeatUsageAndPrice() {
    let charges: Charge[] = [];

    this.customerService.currentPrice?.breakdown.forEach((breakdown: Breakdown) => {
      if (!breakdown.plan_id) {
        return;
      }
      breakdown.charges.forEach((charge) => {
        charge.planId = (breakdown.plan_id as $TSFixMe).toString();
        charges.push(charge);
      });
    });

    let adminsCount = this.app.humanAdmins.length;

    let seatCharge = charges.find((charge) => charge.pricing_metric === Metric.core_seat_count);

    return {
      seatCount: seatCharge?.actual_usage || adminsCount || 1,
      unitPrice: (seatCharge?.per_unit_price || 0) / 100, // Convert cents to dollars. 100 cents = 1 dollar
    };
  }

  get availableDiscountOptions() {
    let options = [];
    let showDiscountOption = this.isOnSelfServeMonthlyPlan && !this.hasDiscountApplied;
    let showDowngradeOption = !this.isOnLowestPlan;
    let showRemoveSeatsOption = !this.hasLowestSeats;

    if (showDiscountOption) {
      options.push(DISCOUNT_OPTIONS.claimDiscount);
    }
    if (showDowngradeOption) {
      options.push(DISCOUNT_OPTIONS.downgradePlan);
    }
    if (showRemoveSeatsOption) {
      options.push(DISCOUNT_OPTIONS.removeUnusedSeats);
    }
    if (
      options.length === 2 &&
      showDowngradeOption &&
      showRemoveSeatsOption &&
      !showDiscountOption
    ) {
      options = [DISCOUNT_OPTIONS.removeUnusedSeats, DISCOUNT_OPTIONS.downgradePlan]; // This is a special case where we want to show the remove seats option first
    }
    return options;
  }

  get banneredOption() {
    return this.availableDiscountOptions[0];
  }

  get otherOptions() {
    return this.availableDiscountOptions.slice(1);
  }

  get downgradePlanOption(): Record<string, string> {
    let planName = null;
    if (this.availableLowerPlan) {
      planName = this.intl.t(this.availableLowerPlan?.nameTranslationKey);
    } else {
      planName = this.intl.t(
        'billing.settings.reduce-costs-modal.cards.downgrade-plan.plan-name-fallback',
      );
    }

    let description = this.intl.t(
      'billing.settings.reduce-costs-modal.cards.downgrade-plan.description',
      { planName },
    );
    if (this.availableDiscountOptions.length > 2) {
      description = this.intl.t(
        'billing.settings.reduce-costs-modal.cards.downgrade-plan.short-description',
        { planName },
      );
    }

    return {
      id: 'downgrade-plan',
      title: this.intl.t('billing.settings.reduce-costs-modal.cards.downgrade-plan.title'),
      description,
      cta: this.intl.t('billing.settings.reduce-costs-modal.cards.downgrade-plan.cta', {
        planName,
      }),
      image: assetUrl(`/assets/images/${IMAGE_ASSET_PATHS[DISCOUNT_OPTIONS.downgradePlan]}`),
    };
  }

  get removeUnusedSeatsOption(): Record<string, string> {
    let unitPrice = this.coreSeatUsageAndPrice.unitPrice;

    let description = this.intl.t(
      'billing.settings.reduce-costs-modal.cards.remove-unused-seats.description',
      { unitPrice },
    );
    if (this.availableDiscountOptions.length < 3) {
      description = this.intl.t(
        'billing.settings.reduce-costs-modal.cards.remove-unused-seats.single-option-description',
        { totalPrice: this.totalSeatPrice },
      );
    }
    return {
      id: 'remove-unused-seats',
      title: this.intl.t('billing.settings.reduce-costs-modal.cards.remove-unused-seats.title', {
        unitPrice,
      }),
      description,
      cta: this.intl.t('billing.settings.reduce-costs-modal.cards.remove-unused-seats.cta'),
      image: assetUrl(`/assets/images/${IMAGE_ASSET_PATHS[DISCOUNT_OPTIONS.removeUnusedSeats]}`),
    };
  }

  get claimDiscountOption(): Record<string, string> {
    return {
      id: 'claim-discount',
      title: this.intl.t('billing.settings.reduce-costs-modal.cards.claim-discount.title'),
      description: this.intl.t(
        'billing.settings.reduce-costs-modal.cards.claim-discount.description',
      ),
      cta: this.intl.t('billing.settings.reduce-costs-modal.cards.claim-discount.cta'),
      image: assetUrl(`/assets/images/${IMAGE_ASSET_PATHS[DISCOUNT_OPTIONS.claimDiscount]}`),
    };
  }

  get nextMonthlyInvoiceDate() {
    return moment(this.customerService.customer.subscription.nextMonthlyInvoiceDate).format('ll');
  }

  get isApplyingDiscount() {
    return taskFor(this.applyChurnDiscount).isRunning;
  }

  get hasOnlyOneSuggestion() {
    return this.availableDiscountOptions.length === 1;
  }

  @action
  discountOption(id: string, key: string): string {
    switch (id) {
      case DISCOUNT_OPTIONS.downgradePlan:
        return this.downgradePlanOption[key];
      case DISCOUNT_OPTIONS.removeUnusedSeats:
        return this.removeUnusedSeatsOption[key];
      case DISCOUNT_OPTIONS.claimDiscount:
      default:
        return this.claimDiscountOption[key];
    }
  }

  @action
  otherOptionStyling(index: number) {
    if (this.otherOptions.length === 1) {
      return 'w-full';
    }
    return `w-1/2 ${index % 2 === 0 ? 'pr-6' : 'pl-6 border-l border-neutral-container-emphasis'}`;
  }

  @action
  handleCtaClick(option: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: option,
      section: 'cancel_subscription.reduce_costs_modal',
      place: 'billing_settings',
    });
    switch (option) {
      case DISCOUNT_OPTIONS.downgradePlan:
        this.openDowngradePlanPaywallModal();
        break;
      case DISCOUNT_OPTIONS.removeUnusedSeats:
        this.router.transitionTo('apps.app.settings.workspace.teammates.index');
        break;
      case DISCOUNT_OPTIONS.claimDiscount:
        this.args.setConfirmDiscountModalVisibility(true);
        break;
    }
  }

  @action
  applyDiscount() {
    taskFor(this.applyChurnDiscount).perform();
  }

  @action
  closeConfirmDiscountModal() {
    this.args.setConfirmDiscountModalVisibility(false);
  }

  @dropTask *applyChurnDiscount() {
    try {
      if (this.customerService.customer?.subscriptionWillBeCancelled) {
        //Restart subscription if it is pending cancellation
        // @ts-ignore
        let customer: any = yield post(
          `/ember/customers/${this.customerService.customer.id}/resume_pending_cancellation`,
          {
            app_id: this.app.id,
          },
        );
        this.subscriptionEndingService.updateCustomerSubscriptionWillBeCancelledAt(
          customer.subscriptionWillBeCancelledAt,
        );
        this.notificationsService.notifyConfirmation(
          this.intl.t(
            'pricing-and-packaging.subscription_ending_warning_banner.modal.confirmation_message',
          ),
        );
      }
      let result: { success: boolean } = yield post('/ember/customers/claim_churn_discount', {
        app_id: this.app.id,
        admin_id: this.app.currentAdmin?.id,
      });
      this.intercomEventService.trackAnalyticsEvent({
        action: 'applied',
        object: 'churn_discount',
        section: 'cancel_subscription.reduce_costs_modal.confirm_discount_modal',
        place: 'billing_settings',
        context: 'self_serve_monthly_churn_discount_claim',
      });
      if (result.success) {
        this.args.setConfirmDiscountModalVisibility(false);
        this.args.onModalClose('churn_discount_applied');
        taskFor(this.customerService.fetchCustomerTask).perform(this.app.customerId);
        this.notificationsService.notifyConfirmation(
          this.intl.t(
            'billing.settings.reduce-costs-modal.cards.claim-discount.success-notification',
          ),
        );
      }
    } catch (e) {
      console.error(e);
      this.args.setConfirmDiscountModalVisibility(false);
      this.intercomEventService.trackAnalyticsEvent({
        action: 'failed',
        object: 'churn_discount',
        section: 'cancel_subscription.reduce_costs_modal.confirm_discount_modal',
        place: 'billing_settings',
        context: 'self_serve_monthly_churn_discount_claim',
      });
      this.notificationsService.notifyError(
        this.intl.t('billing.settings.reduce-costs-modal.cards.claim-discount.error-notification'),
      );
    }
  }

  @action
  openDowngradePlanPaywallModal() {
    this.showEditPlanModal = true;
  }

  @action
  closeDowngradePlanPaywallModal() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'continue_to_downgrade_plan_paywall',
      section: 'cancel_subscription.reduce_costs_modal',
      place: 'billing_settings',
    });
    this.showEditPlanModal = false;
  }

  @action
  handleCancellation() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'continue_to_cancel_subscription',
      section: 'cancel_subscription.reduce_costs_modal',
      place: 'billing_settings',
    });
    if (this.app.canSeeSelfServeChurnSurvey) {
      // startSurvey(SELF_SERVE_CHURN_SURVEY_ID);
      launchSurveyAndTrack(SELF_SERVE_CHURN_SURVEY_ID, this.app, this.router);
      this.args.onModalClose('tour_triggered');
    } else {
      this.router.transitionTo('apps.app.settings.subscription.billing.cancel', this.app.id); // Fallback for EU/AU workspaces
    }
  }

  @action
  handleModalClose(object: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object,
      section: 'cancel_subscription.reduce_costs_modal',
      place: 'billing_settings',
      context: 'abort_cancel_subscription',
    });
    this.args.onModalClose('abort_cancellation');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Billing::Settings::Cancel::ReduceCostsModal': typeof ReduceCostsModal;
    'billing/settings/cancel/reduce-costs-modal': typeof ReduceCostsModal;
  }
}
